import type { App } from 'vue';
import mixpanel from 'mixpanel-browser';
import { isEmpty, snakeCase } from 'lodash-es';
import Division from '@/entities/Division.js';
import Role from '@/entities/Role.js';
import { capitalizeFirst } from '@/utils/string.js';
import { useAccountStore } from '@/store/modules/account/store.js';
import { useGtm } from '@gtm-support/vue-gtm';
import type { GtmSupport } from '@gtm-support/core';

interface MixpanelUser {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    created_at: string;
    timezone: string;
    locale: string;
    sex: string;
    role_id: number;
    divisions: number[];
    post?: { name: string } | null;
    account: {
        name: string;
        province: string;
    };
}

type KeyTransformer = (key: string) => string;

const MixpanelClient = {
    isInitialized: false,
    gtm: null as GtmSupport | null | undefined,
    user: null as MixpanelUser | null,

    initGtm(): void {
        if (this.gtm?.enabled()) {
            return;
        }

        this.gtm = useGtm();
    },

    init(): void {
        if (this.isInitialized) {
            return;
        }

        if (['production'].includes(process.env.VUE_APP_ENV ?? '')) {
            mixpanel.init(process.env.VUE_APP_MIXPANEL_TOKEN ?? '');

            this.isInitialized = true;
        }
    },

    async setAuthUser(user: MixpanelUser | null): Promise<void> {
        if (!this.isInitialized) {
            return;
        }

        if (!user || isEmpty(user)) {
            mixpanel.identify(undefined);
            return;
        }

        this.user = user;

        // Id
        mixpanel.identify(String(user.id));

        // Attributes
        mixpanel.people.set(
            this.preparePayload({
                $email: user.email,
                $first_name: user.first_name,
                $last_name: user.last_name,
                $created: user.created_at,
                $timezone: user.timezone,
                locale: user.locale,
                dealer: user.account.name,
                sex: user.sex,
                division: user.divisions.map((divisionId: number) => Division.getKey(divisionId)),
                role: Role.getKey(user.role_id),
                post: user.post?.name || 'programmer',
            })
        );

        this.group('platform', 'web');
        this.group('environment', process.env.VUE_APP_ENV ?? '');
        this.group('province', user.account.province);
    },

    track(event: string, data: Record<string, unknown>): void {
        if (!this.isInitialized) {
            return;
        }

        this.initGtm();

        const contextAccount = useAccountStore().contextAccount;

        mixpanel.track(capitalizeFirst(event), this.preparePayload(data));

        if (this.gtm?.enabled()) {
            window.dataLayer?.push({
                event: snakeCase(event),
                province: this.user?.account?.province,
                environment: process.env.VUE_APP_ENV,
                platform: 'web',
                distinct_id: this.user?.id,
                role: Role.getKey(this.user?.role_id),
                division: this.user?.divisions
                    ?.map((divisionId: number) => Division.getKey(divisionId))
                    ?.join(','),
                post: this.user?.post?.name || 'programmer',
                tenant_name: contextAccount?.name,
                tenant_id: contextAccount?.id,
                ...this.preparePayload(data, snakeCase),
            });
        }
    },

    group(group: string, list: string | string[]): void {
        if (!this.isInitialized) {
            return;
        }

        mixpanel.set_group(capitalizeFirst(group), list);
    },

    preparePayload(
        payload: Record<string, unknown>,
        keyTransformer: KeyTransformer = capitalizeFirst
    ): Record<string, unknown> {
        const formattedPayload: Record<string, unknown> = {};

        for (const [key, value] of Object.entries(payload)) {
            formattedPayload[keyTransformer(key)] = value ?? '';
        }
        return formattedPayload;
    },
};

MixpanelClient.init();

export default MixpanelClient;

export const vueMixpanelPlugin = {
    install(app: App) {
        app.config.globalProperties.$behavior = MixpanelClient;
        app.provide('$behavior', MixpanelClient);
    },
};
